import { FormConfig } from '../types';

// Helper functions to generate exact same structure
const createBasicFields = (includePhone = true) => [
  { name: `date`, label: "Datum", type: "date", required: true },
  { name: `time`, label:"Tijd", type: "time", required: true },
  { name: `driver_name`, label: "Naam Chauffeur", type: "text", required: true },
  { name: `customer_name`, label: "Naam Klant", type: "text", required: true },
  ...(includePhone ? [{ name: `phone_number`, label: "Telefoonnummer", type: "tel", required: true, placeholder: "06-12345678", fullWidth: true }] : []),
  { name: `address`, label: "Adres", type: "text", required: true, fullWidth: true },
];

const createVehicleFields = (prefix = '') => [
  { name: `${prefix}license_number`, label: prefix ? "Kenteken (retour)" : "Kenteken", type: "text", required: true, placeholder: "XX-XX-XX" },
  { name: `${prefix}car_brand`, label: prefix ? "Auto merk (retour)" : "Auto merk", type: "text", required: true },
  { name: `${prefix}car_type`, label: prefix ? "Auto type (retour)" : "Auto type", type: "text", required: true },
  { name: `${prefix}key_count`, label: prefix ? "Aantal Sleutels (retour)" : "Aantal Sleutels", type: "number", required: true, defaultValue: "2", validation: {
    min: 0,
    custom: (value: any) => {
      const numValue = Number(value);
      if (!isNaN(numValue) && numValue < 0) {
        return 'Aantal sleutels kan niet negatief zijn';
      }
      return null;
    }
  }},
  { name: `${prefix}notes`, label: prefix ? "Opmerkingen (retour)" : "Opmerkingen", type: "textarea", required: false, placeholder: "Eventuele opmerkingen...", fullWidth: true },
];

const createVehicleFieldsWithDamage = (prefix = '') => [
  { name: `${prefix}license_number`, label: prefix ? "Kenteken (retour)" : "Kenteken", type: "text", required: true, placeholder: "XX-XX-XX" },
  { name: `${prefix}car_brand`, label: prefix ? "Auto merk (retour)" : "Auto merk", type: "text", required: true },
  { name: `${prefix}car_type`, label: prefix ? "Auto type (retour)" : "Auto type", type: "text", required: true },
  { name: `${prefix}key_count`, label: prefix ? "Aantal Sleutels (retour)" : "Aantal Sleutels", type: "number", required: true, defaultValue: "2", validation: {
    min: 0,
    custom: (value: any) => {
      const numValue = Number(value);
      if (!isNaN(numValue) && numValue < 0) {
        return 'Aantal sleutels kan niet negatief zijn';
      }
      return null;
    }
  }},
  { name: `${prefix}damage`, label: prefix ? "Schade (retour)" : "Schade", type: "textarea", required: false, placeholder: "Beschrijf eventuele schade aan het voertuig...", fullWidth: true },
];

// Combined exterior and wheel images in logical order around the car
// Order: front -> right side -> wheel R-front -> wheel R-rear -> rear -> left side -> wheel L-rear -> wheel L-front
const createExteriorAndWheelImages = (prefix = '', suffix = '', required = true) => [
  { name: `${prefix}front`, label: `Voorkant${suffix}`, instructions: "Maak een duidelijke foto van de voorkant van het voertuig.", required },
  { name: `${prefix}right_side`, label: `Rechterzijde${suffix}`, instructions: "Maak een foto van de volledige rechterzijde van het voertuig.", required },
  { name: `${prefix}wheel_front_right`, label: `Velg voorkant rechts${suffix}`, instructions: "Maak een foto van de voorste rechtervelk.", required },
  { name: `${prefix}wheel_back_right`, label: `Velg achterkant rechts${suffix}`, instructions: "Maak een foto van de achterste rechtervelk.", required },
  { name: `${prefix}back`, label: `Achterkant${suffix}`, instructions: "Maak een duidelijke foto van de achterkant van het voertuig.", required },
  { name: `${prefix}left_side`, label: `Linkerzijde${suffix}`, instructions: "Maak een foto van de volledige linkerzijde van het voertuig.", required },
  { name: `${prefix}wheel_back_left`, label: `Velg achterkant links${suffix}`, instructions: "Maak een foto van de achterste linkervelk.", required },
  { name: `${prefix}wheel_front_left`, label: `Velg voorkant links${suffix}`, instructions: "Maak een foto van de voorste linkervelk.", required },
];

// Helper function to create mileage field
const createMileageField = (prefix = '') => {
  let label = "Kilometerstand";
  if (prefix === 'new_') {
    label = "Kilometerstand (nieuw)";
  } else if (prefix === 'return_') {
    label = "Kilometerstand (retour)";
  }
  
  return {
    name: `${prefix}mileage`,
    label,
    type: "number" as const,
    required: true,
    validation: {
      min: 0,
      custom: (value: any) => {
        const numValue = Number(value);
        if (!isNaN(numValue) && numValue < 0) {
          return 'Kilometerstand kan niet negatief zijn';
        }
        return null;
      }
    }
  };
};

const createDetailImages = (prefix = '', suffix = '', required = true) => [
  { name: `${prefix}interior`, label: `Interieur${suffix}`, instructions: "Maak een foto van het interieur.", required },
  { name: `${prefix}dashboard`, label: `Dashboard met kilometerstand${suffix}`, instructions: "Maak een foto van het dashboard met zichtbare kilometerstand.", required },
  { name: `${prefix}keys`, label: `Sleutels${suffix}`, instructions: "Maak een foto van de sleutels.", required },
  { name: `${prefix}registration`, label: `Kentekenbewijs${suffix}`, instructions: "Maak een foto van het kentekenbewijs.", required },
];

const createDetailImagesWithDamage = (prefix = '', suffix = '', required = true) => [
  { name: `${prefix}interior`, label: `Interieur${suffix}`, instructions: "Maak een foto van het interieur.", required },
  { name: `${prefix}dashboard`, label: `Dashboard met kilometerstand${suffix}`, instructions: "Maak een foto van het dashboard met zichtbare kilometerstand.", required },
  { name: `${prefix}keys`, label: `Sleutels${suffix}`, instructions: "Maak een foto van de sleutels.", required },
  { name: `${prefix}registration`, label: `Kentekenbewijs${suffix}`, instructions: "Maak een foto van het kentekenbewijs.", required },
  { name: `${prefix}damage`, label: `Schade met doorberekening ER${suffix}`, instructions: "Maak fotos van eventuele schade.", required: false },
];

const createImageSections = (prefix = '', suffix = '') => [
  {
    title: `Stap 1: Exterieur en Velgen${suffix}`,
    accent: "blue",
    images: createExteriorAndWheelImages(prefix, suffix),
  },
  {
    title: `Stap 2: Details${suffix}`,
    accent: "green",
    images: createDetailImages(prefix, suffix),
  },
];

const createImageSectionsWithDamage = (prefix = '', suffix = '', required = true) => [
  {
    title: `Stap 1: Exterieur en Velgen${suffix}`,
    accent: "blue",
    images: createExteriorAndWheelImages(prefix, suffix, required),
  },
  {
    title: `Stap 2: Details${suffix}`,
    accent: "green",
    images: createDetailImagesWithDamage(prefix, suffix, required),
  },
];

const createSignatures = () => ({
  title: "Handtekeningen",
  fields: [
    { name: "driver_signature", label: "Handtekening Chauffeur", required: true },
    { name: "customer_signature", label: "Handtekening Klant", required: true }
  ]
});

const createSignaturesWithInfo = () => ({
  title: "Handtekeningen",
  fields: [
    { name: "driver_signature", label: "Handtekening Chauffeur", required: true },
    { name: "customer_signature", label: "Handtekening Klant", required: true }
  ],
  infoText: "Ondertekening is geen definitieve bevestiging van inname."
});

// Form configurations
export const ophaalFormConfig: FormConfig = [
  {
    section: "Basisgegevens",
    fields: createBasicFields(),
  },
  {
    section: "Voertuiggegevens",
    fields: createVehicleFields(),
  },
  {
    section: "Foto's van het voertuig",
    fields: [createMileageField()],
    imageSections: createImageSections(),
  },
];
ophaalFormConfig.signatures = createSignaturesWithInfo();

export const afleverFormConfig: FormConfig = [
  {
    section: "Basisgegevens uitgifte",
    fields: createBasicFields(),
  },
  {
    section: "Voertuiggegevens uitgifte",
    fields: createVehicleFields(),
  },
  {
    section: "Foto's van het voertuig (uitgifte)",
    fields: [createMileageField()],
    imageSections: createImageSections(),
  },
];
afleverFormConfig.signatures = createSignatures();

export const innameFormConfig: FormConfig = [
  {
    section: "Basisgegevens inname",
    fields: createBasicFields(false),
  },
  {
    section: "Voertuiggegevens inname",
    fields: createVehicleFieldsWithDamage(),
  },
  {
    section: "Foto's van het voertuig (inname)",
    fields: [createMileageField()],
    imageSections: createImageSectionsWithDamage('', '', false),
  },
];

export const omruilFormConfig: FormConfig = [
  {
    section: "Gedeelde informatie",
    fields: [
      { name: "date", label: "Datum", type: "date", required: true },
      { name: "time", label: "Tijd", type: "time", required: true },
      { name: "driver_name", label: "Naam Chauffeur", type: "text", required: true },
      { name: "customer_name", label: "Naam Klant", type: "text", required: true },
      { name: "phone_number", label: "Telefoonnummer", type: "tel", required: true, placeholder: "06-12345678", fullWidth: true },
      { name: "address", label: "Adres", type: "text", required: true, fullWidth: true },
    ],
  },
  {
    section: "Uitgifte voertuig (nieuw)",
    fields: [
      { name: "new_license_number", label: "Kenteken (nieuw)", type: "text", required: true, placeholder: "XX-XX-XX" },
      { name: "new_car_brand", label: "Auto merk (nieuw)", type: "text", required: true },
      { name: "new_car_type", label: "Auto type (nieuw)", type: "text", required: true },
      { name: "new_key_count", label: "Aantal Sleutels (nieuw)", type: "number", required: true, defaultValue: "2", validation: {
        min: 0,
        custom: (value: any) => {
          const numValue = Number(value);
          if (!isNaN(numValue) && numValue < 0) {
            return 'Aantal sleutels kan niet negatief zijn';
          }
          return null;
        }
      }},
      { name: "new_notes", label: "Opmerkingen (nieuw)", type: "textarea", required: false, placeholder: "Eventuele opmerkingen...", fullWidth: true },
      createMileageField('new_'),
    ],
    imageSections: createImageSections('new_', ' (nieuw)'),
  },
  {
    section: "Inname voertuig (retour)",
    fields: [
      { name: "return_license_number", label: "Kenteken (retour)", type: "text", required: true, placeholder: "XX-XX-XX" },
      { name: "return_car_brand", label: "Auto merk (retour)", type: "text", required: true },
      { name: "return_car_type", label: "Auto type (retour)", type: "text", required: true },
      { name: "return_key_count", label: "Aantal Sleutels (retour)", type: "number", required: true, defaultValue: "2", validation: {
        min: 0,
        custom: (value: any) => {
          const numValue = Number(value);
          if (!isNaN(numValue) && numValue < 0) {
            return 'Aantal sleutels kan niet negatief zijn';
          }
          return null;
        }
      }},
      { name: "return_damage", label: "Schade (retour)", type: "textarea", required: false, placeholder: "Beschrijf eventuele schade aan het voertuig...", fullWidth: true },
      { name: "return_notes", label: "Opmerkingen (retour)", type: "textarea", required: false, placeholder: "Eventuele opmerkingen...", fullWidth: true },
      createMileageField('return_'),
    ],
    imageSections: createImageSections('return_', ' (retour)'),
  },
];
omruilFormConfig.signatures = createSignaturesWithInfo();